import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Calendar,
  BookOpen,
  MessageSquare,
  User,
  Settings,
  LogOut,
  Bell,
  Menu,
  X,
} from "lucide-react";
import { useProfileData } from "../../../hooks/useProfileData";
import { useAuthStore } from "../../../hooks/useAuthStore";
import { useQueryClient } from "@tanstack/react-query";
import {
  useNotificationsQuery,
  useUnreadNotificationsCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} from "../../../api/query/useNotificationQueries";
import socketService from "../../../api/socket";
import {
  formatNotificationTime,
} from "../../../utils/notifications";
import NotificationAvatar from "../../../components/NotificationAvatar";
import { useAppConfigContext } from "../../../context/AppConfigContext";

function AssistantDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const queryClient = useQueryClient();
  const { data: notificationsData } = useNotificationsQuery();
  const notifications = notificationsData?.data?.items || [];

  const { data: unreadCountData } = useUnreadNotificationsCountQuery();
  const unreadNotificationsCount = unreadCountData?.data?.count || 0;

  const markAllNotificationsReadMutation = useMarkAllNotificationsReadMutation();
  const markNotificationReadMutation = useMarkNotificationReadMutation();

  const { actions } = useAuthStore();
  const { config } = useAppConfigContext();

  const { isProfileLoading } = useProfileData();
  const dashboardContext = { isProfileLoading };

  const navItems = [
    {
      label: "Dashboard",
      to: "/dashboards/assistant",
      icon: LayoutGrid,
      end: true,
      activePaths: ["/dashboards/assistant", "/dashboards/assistant/overview"],
    },
    { label: "Trips", to: "/dashboards/assistant/tours", icon: Calendar },
    { label: "Bookings", to: "/dashboards/assistant/bookings", icon: BookOpen },
    { label: "Messages", to: "/dashboards/assistant/messages", icon: MessageSquare },
    { label: "Profile", to: "/dashboards/assistant/profile", icon: User },
    { label: "Settings", to: "/dashboards/assistant/settings", icon: Settings },
  ];

  const closeSidebar = () => setIsSidebarOpen(false);
  const closeNotifications = () => setIsNotificationsOpen(false);

  const toggleNotifications = () => {
    closeSidebar();
    setIsNotificationsOpen((prev) => !prev);
  };

  const markAllNotificationsAsRead = () => {
    markAllNotificationsReadMutation.mutate();
  };

  const handleMarkOneAsRead = (id) => {
    markNotificationReadMutation.mutate(id);
  };

  useEffect(() => {
    socketService.connect();

    const handleNotificationReceived = (notification) => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });

      // An approval verdict is decided on the admin's machine, so this push is
      // the only signal this tab gets. Without refreshing the profile the
      // approval banners keep showing stale state until a manual page reload.
      if (notification?.type === "profile_review") {
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["expertise"] });
      }
    };

    const handleMessageReceived = () => {
      queryClient.invalidateQueries({ queryKey: ["messages", "unreadCount"] });
    };

    socketService.on("notification", handleNotificationReceived);
    socketService.on("message", handleMessageReceived);

    return () => {
      socketService.off("notification", handleNotificationReceived);
      socketService.off("message", handleMessageReceived);
    };
  }, [queryClient]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsSidebarOpen(false);
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleLogout = async () => {
    await actions.logout();
    socketService.disconnect();
    closeSidebar();
    navigate("/auth/login");
  };

  return (
    <div className="assistant-dashboard-viewport flex flex-col bg-white text-gray-900">
      <div
        className={`flex h-full flex-col transition-[filter,opacity] duration-200 ${
          isNotificationsOpen ? "pointer-events-none blur-[4px] brightness-[0.58]" : ""
        }`}
      >
        <header className="flex h-20 items-center justify-between border-b bg-white px-4 sm:px-8">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <img
              src={config.logoUrl || "/images/logo.png"}
              alt={config.appName || "PULO"}
              className="h-7 w-auto sm:h-8"
            />
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggleNotifications}
              className="relative flex h-9 w-9 items-center justify-center rounded-full border border-[#D6DCE4] bg-white text-[#4B5565] transition hover:bg-gray-50 sm:h-10 sm:w-10"
              aria-label="Notifications"
              aria-expanded={isNotificationsOpen}
            >
              <Bell size={16} strokeWidth={1.9} />
              {unreadNotificationsCount > 0 ? (
                <span className="absolute right-0 top-0 inline-flex h-4 min-w-4 translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-[#F59E0B] px-1 text-[10px] font-semibold text-white">
                  {unreadNotificationsCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => {
                closeNotifications();
                setIsSidebarOpen((prev) => !prev);
              }}
              className="rounded-md p-2 text-gray-600 hover:bg-gray-100 lg:hidden"
              aria-label="Toggle sidebar"
            >
              {isSidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 bg-gray-50">
          <div
            className={`fixed inset-x-0 bottom-0 top-20 z-40 bg-black/40 transition-opacity lg:hidden ${
              isSidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            onClick={closeSidebar}
          />
          <aside
            className={`assistant-dashboard-sidebar fixed left-0 top-20 z-50 flex w-56 flex-col overflow-y-auto border-r bg-white px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-5 transition-transform lg:static lg:h-full lg:w-[280px] lg:px-8 lg:py-10 lg:translate-x-0 ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <nav className="flex-1 space-y-1.5 lg:space-y-3">
              {navItems.map(({ label, to, icon: Icon, end, activePaths }) => {
                const isItemActive =
                  activePaths?.includes(location.pathname) ?? false;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    onClick={closeSidebar}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition lg:gap-4 lg:rounded-xl lg:px-5 lg:py-3.5 lg:text-[17px] lg:leading-7 ${
                        isItemActive || isActive
                          ? "bg-[#61013B] text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    <Icon size={16} className="shrink-0 lg:h-5 lg:w-5" />
                    {label}
                  </NavLink>
                );
              })}
            </nav>

            <button
              type="button"
              onClick={handleLogout}
              className="mt-auto flex items-center gap-2.5 rounded-lg border border-red-100 px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 lg:hidden"
            >
              <LogOut size={16} className="shrink-0" />
              Logout
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="mt-6 hidden items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 lg:flex lg:mt-10 lg:gap-4 lg:rounded-xl lg:px-5 lg:py-3.5 lg:text-[17px] lg:leading-7"
            >
              <LogOut size={16} className="shrink-0 lg:h-5 lg:w-5" />
              Logout
            </button>
          </aside>

          <main className="flex-1 overflow-y-auto p-3 sm:p-6">
            <Outlet context={dashboardContext} />
          </main>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-40 bg-[#0F172A]/24 transition-opacity ${
          isNotificationsOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeNotifications}
      />
      <aside
          className={`fixed inset-y-0 right-0 z-50 flex h-screen w-full max-w-[400px] flex-col overflow-hidden border-l border-[#EAECF0] bg-white shadow-[-20px_0_60px_rgba(16,24,40,0.18)] transition-transform duration-200 ${
            isNotificationsOpen
              ? "translate-x-0"
              : "pointer-events-none translate-x-full"
          }`}
          aria-hidden={!isNotificationsOpen}
        >
          <div className="border-b border-[#F2F4F7] px-5 py-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[18px] font-semibold leading-none text-[#101828]">
                  Notifications
                </h2>
                <p className="mt-3 text-sm text-[#98A2B3]">
                  Stay updated with important alerts and activity
                </p>
              </div>

              <button
                type="button"
                onClick={closeNotifications}
                className="rounded-md p-1.5 text-[#98A2B3] transition hover:bg-gray-50 hover:text-[#475467]"
                aria-label="Close notifications"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end border-b border-[#F2F4F7] px-5 py-4">
            <button
              type="button"
              onClick={markAllNotificationsAsRead}
              className="text-sm font-semibold text-[#61013B] disabled:cursor-not-allowed disabled:text-[#98A2B3]"
              disabled={unreadNotificationsCount === 0}
            >
              Mark all as read
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notification, index) => {
                const hasUnread = !notification.isRead;
                return (
                  <div
                    key={notification._id || notification.id}
                    onClick={() => {
                      if (hasUnread) {
                        handleMarkOneAsRead(notification._id);
                      }
                    }}
                    className={`flex gap-3 px-5 py-5 transition ${
                      hasUnread ? "bg-[#FAF7F9] hover:bg-[#F5EFF3]" : "hover:bg-gray-50"
                    } ${
                      index < notifications.length - 1 ? "border-b border-[#F2F4F7]" : ""
                    } cursor-pointer`}
                  >
                    <div className="flex pt-2.5">
                      <span
                        className={`h-3 w-3 rounded-full ${
                          hasUnread ? "bg-[#7A0147]" : "bg-[#EAECF0]"
                        }`}
                      />
                    </div>

                    <NotificationAvatar
                      notification={notification}
                      className="h-14 w-14"
                    />

                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold leading-relaxed text-[#101828]">
                        {notification.title}
                      </p>
                      <p className="mt-1 text-sm font-medium leading-relaxed text-[#475467]">
                        {notification.body}
                      </p>
                      <p className="mt-2 text-sm font-medium text-[#98A2B3]">
                        {formatNotificationTime(notification.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex h-full items-center justify-center px-6 py-10 text-center">
                <p className="text-sm font-medium text-[#667085]">
                  You have no notifications yet.
                </p>
              </div>
            )}
          </div>
        </aside>
    </div>
  );
}

export default AssistantDashboardLayout;
